/* src/components/ui/ConfirmationModal.js */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';

/**
 * A reusable confirmation modal component.
 * Supports standard confirmation and optional text input confirmation.
 */
const ConfirmationModal = ({
    isOpen,
    onClose, // Standard prop for closing/cancelling the modal
    onConfirm,
    title,
    children, // Main content/message (passed between tags) - preferred way
    confirmText = "Confirm",
    cancelText = "Cancel",
    confirmColor = "indigo",
    isDestructive = false,
    // Props for input confirmation (passed from parent)
    requiredInputText = null, // The text required to enable confirm (if null, no input required)
    inputLabel = "Type the required text to confirm:", // Label for the input
    inputValue = "", // Controlled input value from parent
    onInputChange = () => {}, // Handler from parent to update input value
    confirmationPlaceholder = "", // Placeholder for input (optional)
    // Deprecated/Alternative prop (keep for potential backward compatibility if needed elsewhere, but children is preferred)
    message,
}) => {

    // Determine if input is required based on requiredInputText prop
    const isInputRequired = requiredInputText !== null && requiredInputText !== undefined;

    // State to track if the confirmation condition is met
    // True if input isn't required, OR if input is required AND matches
    const [isConfirmed, setIsConfirmed] = useState(!isInputRequired);

    // Effect to re-evaluate confirmation state when input value or required text changes
    useEffect(() => {
        if (isInputRequired) {
            // Compare parent's controlled inputValue with the required text
            setIsConfirmed(inputValue.trim().toLowerCase() === requiredInputText.trim().toLowerCase());
        } else {
            setIsConfirmed(true); // Always confirmed if input not required
        }
    }, [inputValue, requiredInputText, isInputRequired]); // Rerun when these change

    // Effect to reset confirmation state when modal opens/closes or requirement changes
    // Primarily ensures button state is correct when opening
     useEffect(() => {
        if (isOpen) {
            setIsConfirmed(!isInputRequired); // Reset based on current requirement
             // Note: We don't reset the parent's inputValue here; parent controls it.
        }
    }, [isOpen, isInputRequired]);

    // Handler for the main confirmation button
    const handleConfirm = () => {
        // Only call the parent's onConfirm if confirmation condition is met
        if (isConfirmed) {
            onConfirm();
        } else {
            console.warn("ConfirmationModal: Confirm clicked but validation failed.");
            // Optionally add visual feedback like shaking the modal/input
        }
    };

    // Use the confirmColor OR isDestructive prop to decide button color
    const finalConfirmColor = isDestructive ? 'red' : confirmColor;

    // --- Styling ---
    const confirmButtonBaseClasses = "inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 sm:text-sm transition-opacity duration-150";
    const confirmButtonColorClasses = {
        indigo: `bg-indigo-600 hover:bg-indigo-700 focus:ring-indigo-500 ${!isConfirmed ? 'opacity-50 cursor-not-allowed' : ''}`,
        red: `bg-red-600 hover:bg-red-700 focus:ring-red-500 ${!isConfirmed ? 'opacity-50 cursor-not-allowed' : ''}`,
    };
    const cancelButtonClasses = "mt-3 sm:mt-0 inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:text-sm";

    if (!isOpen) return null;

    // --- Render Modal ---
    return (
        <div className="fixed z-50 inset-0 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                {/* Background overlay */}
                <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={onClose}></div>
                {/* Centering trick */}
                <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">​</span>

                {/* Modal panel */}
                <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                    <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                        <div className="sm:flex sm:items-start">
                            {/* Icon (show if destructive) */}
                            {isDestructive && (
                                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                                    <FontAwesomeIcon icon={faExclamationTriangle} className="h-6 w-6 text-red-600" aria-hidden="true" />
                                </div>
                            )}
                            <div className={`mt-3 text-center sm:mt-0 ${isDestructive ? 'sm:ml-4' : ''} sm:text-left w-full`}>
                                <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                                    {title}
                                </h3>
                                <div className="mt-2 text-sm text-gray-600 space-y-2">
                                    {/* Render children passed from parent (preferred) OR message prop */}
                                    {children || message}
                                </div>

                                {/* Confirmation Input Section (conditional based on requiredInputText) */}
                                {isInputRequired && (
                                    <div className="mt-4">
                                        {/* Use inputLabel prop */}
                                        <label htmlFor="confirmation-input" className="block text-sm font-medium text-gray-700 mb-1">
                                            {inputLabel || `Type "${requiredInputText}" to confirm:`}
                                        </label>
                                        <input
                                            type="text"
                                            id="confirmation-input"
                                            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                            // Use parent's value and handler
                                            value={inputValue}
                                            onChange={onInputChange}
                                            placeholder={confirmationPlaceholder || requiredInputText} // Use placeholder or required text
                                            autoComplete="off" autoCapitalize="off" spellCheck="false"
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    {/* Modal Footer */}
                    <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                        <button
                            type="button"
                            // Use finalConfirmColor and isConfirmed for styling/disabling
                            className={`${confirmButtonBaseClasses} ${confirmButtonColorClasses[finalConfirmColor] || confirmButtonColorClasses.indigo} w-full sm:ml-3 sm:w-auto`}
                            onClick={handleConfirm}
                            disabled={!isConfirmed} // Disable button if input required and not matched
                        >
                            {confirmText}
                        </button>
                        <button
                            type="button"
                            className={`${cancelButtonClasses} w-full sm:w-auto`}
                            // Critical: Ensure this button calls onClose prop for cancelling/closing
                            onClick={onClose}
                        >
                            {cancelText}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Update PropTypes to reflect the props actually used for input control and standard usage
ConfirmationModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired, // Standard handler for closing/cancelling
    onConfirm: PropTypes.func.isRequired,
    title: PropTypes.string.isRequired,
    children: PropTypes.node, // Preferred way to pass message content
    message: PropTypes.string, // Kept for backward compatibility if used elsewhere
    confirmText: PropTypes.string,
    cancelText: PropTypes.string,
    confirmColor: PropTypes.oneOf(['indigo', 'red']),
    isDestructive: PropTypes.bool,
    // Props for input confirmation
    requiredInputText: PropTypes.string, // Text to match (if provided, input is required)
    inputLabel: PropTypes.string, // Label for the input field
    inputValue: PropTypes.string, // Controlled input value from parent
    onInputChange: PropTypes.func, // Parent handler to update inputValue
    confirmationPlaceholder: PropTypes.string,
};

export default ConfirmationModal;